// Admin redes: configuración Instagram + sync.
// Sprint SS-REDES 2026-05-09.

import { useEffect, useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Instagram, Loader2, AlertCircle, CheckCircle2, RefreshCw, Send, Plug, Save, ExternalLink,
} from 'lucide-react';
import {
  getRedesConfig, updateRedesConfig, probarRedesConexion, sincronizarRedes, refrescarRedesToken,
} from '../../lib/adminApi';
import { ApiError } from '../../lib/api';

export default function AdminRedes() {
  const { t } = useTranslation();
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [handle, setHandle] = useState('');
  const [token, setToken] = useState('');
  const [activo, setActivo] = useState(false);
  const [enviando, setEnviando] = useState(false);
  const [accion, setAccion] = useState(null); // {tipo, msg}
  const [dirty, setDirty] = useState(false);

  const cargar = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      const r = await getRedesConfig();
      setConfig(r.config);
      setHandle(r.config?.instagram_handle || 'anyacontrera');
      setActivo(r.config?.instagram_activo || false);
      setDirty(false);
    } catch (e) {
      setError(e instanceof ApiError ? e.message : t('admin.error_generico'));
    } finally { setLoading(false); }
  }, [t]);

  useEffect(() => { cargar(); }, [cargar]);

  const guardar = async () => {
    setEnviando(true); setAccion(null);
    try {
      const body = {};
      if (handle.trim() !== (config?.instagram_handle || '')) body.instagram_handle = handle.trim();
      if (token.trim()) body.instagram_access_token = token.trim();
      if (activo !== config?.instagram_activo) body.instagram_activo = activo;
      const r = await updateRedesConfig(body);
      setConfig(r.config);
      setToken(''); // Limpiar input por seguridad tras guardar
      setActivo(r.config?.instagram_activo || false);
      setDirty(false);
      setAccion({ tipo: 'ok', msg: t('admin.redes.guardado_ok') });
    } catch (e) {
      const code = e instanceof ApiError ? e.code : null;
      const msg = t('admin.redes.errores.' + code, { defaultValue: e.message || t('admin.redes.error_guardar') });
      setAccion({ tipo: 'err', msg });
    } finally { setEnviando(false); }
  };

  const ejecutarAccion = async (fn, key) => {
    setEnviando(true); setAccion(null);
    try {
      const r = await fn();
      const msg = t('admin.redes.accion_ok.' + key, { defaultValue: t('admin.redes.accion_ok_generico') });
      setAccion({ tipo: 'ok', msg, detalle: r });
      cargar();
    } catch (e) {
      const code = e instanceof ApiError ? e.code : null;
      const msg = t('admin.redes.errores.' + code, { defaultValue: e.message || t('admin.redes.error_guardar') });
      setAccion({ tipo: 'err', msg });
    } finally { setEnviando(false); }
  };

  if (loading) return <div className="admin-loading"><Loader2 size={24} className="spin" /></div>;
  if (error) return <div className="admin-error"><AlertCircle size={20} /><span>{error}</span></div>;

  const tieneToken = config?.instagram_tiene_token === true;
  const cadPronto = config?.instagram_token_caduca_pronto === true;
  const dias = config?.instagram_dias_token_restantes;
  const ultimaSyncOk = config?.instagram_ultima_sync_ok === true;
  const ultimaSyncErr = config?.instagram_ultima_sync_error;

  return (
    <div className="admin-page admin-redes">
      <header className="admin-page-head">
        <div>
          <h1>{t('admin.redes.titulo')}</h1>
          <p className="admin-page-sub">{t('admin.redes.sub')}</p>
        </div>
      </header>

      <section className="admin-card">
        <h2 className="admin-card-titulo">
          <Instagram size={18} strokeWidth={1.5} style={{ verticalAlign: 'text-bottom', marginRight: 6 }} />
          {t('admin.redes.cuenta_titulo')}
        </h2>

        <div className="admin-form-grid one-col">
          <div className="modal-campo">
            <label>{t('admin.redes.handle_label')}</label>
            <input type="text" value={handle}
                   onChange={(e) => { setHandle(e.target.value); setDirty(true); }}
                   maxLength={60}
                   placeholder="anyacontrera" />
            <small className="modal-hint">{t('admin.redes.handle_hint')}</small>
          </div>

          <div className="modal-campo">
            <label>{t('admin.redes.token_label')}</label>
            <input type="password" value={token}
                   onChange={(e) => { setToken(e.target.value); setDirty(true); }}
                   placeholder={tieneToken ? t('admin.redes.token_placeholder_existe') : t('admin.redes.token_placeholder_nuevo')}
                   autoComplete="off" />
            <small className="modal-hint">{t('admin.redes.token_hint')}</small>
          </div>

          <div className="modal-campo">
            <label>
              <input type="checkbox" checked={activo}
                     onChange={(e) => { setActivo(e.target.checked); setDirty(true); }} />
              {' '}{t('admin.redes.activo_label')}
            </label>
          </div>
        </div>

        <div className="modal-acciones" style={{ justifyContent: 'flex-end' }}>
          <button type="button" className="btn-primary"
                  onClick={guardar}
                  disabled={enviando || !dirty}>
            {enviando ? <Loader2 size={16} className="spin" /> : <><Save size={14} /> {t('comun.guardar')}</>}
          </button>
        </div>
      </section>

      <section className="admin-card">
        <h2 className="admin-card-titulo">{t('admin.redes.estado_titulo')}</h2>
        <ul className="admin-redes-estado">
          <li>
            <span>{t('admin.redes.estado.token')}</span>
            <strong className={tieneToken ? 'ok' : 'warn'}>
              {tieneToken ? t('admin.redes.configurado') : t('admin.redes.no_configurado')}
            </strong>
          </li>
          {tieneToken && dias !== null && dias !== undefined && (
            <li>
              <span>{t('admin.redes.estado.caduca')}</span>
              <strong className={cadPronto ? 'warn' : 'ok'}>
                {dias > 0
                  ? t('admin.redes.dias_restantes', { count: dias })
                  : t('admin.redes.token_caducado')}
              </strong>
            </li>
          )}
          <li>
            <span>{t('admin.redes.estado.ultima_sync')}</span>
            <strong className={ultimaSyncOk ? 'ok' : (ultimaSyncErr ? 'err' : 'muted')}>
              {config?.instagram_ultima_sync_at
                ? new Date(config.instagram_ultima_sync_at).toLocaleString()
                : t('admin.redes.nunca')}
              {' '}
              {ultimaSyncOk ? <CheckCircle2 size={12} /> : (ultimaSyncErr ? <AlertCircle size={12} /> : null)}
            </strong>
          </li>
          {ultimaSyncErr && (
            <li>
              <span>{t('admin.redes.estado.ultimo_error')}</span>
              <strong className="err" style={{ fontWeight: 400, fontSize: 12 }}>{ultimaSyncErr}</strong>
            </li>
          )}
          <li>
            <span>{t('admin.redes.estado.posts_cacheados')}</span>
            <strong>{config?.instagram_total_posts_cacheados || 0}</strong>
          </li>
        </ul>

        {accion && (
          <div className={'admin-msg ' + (accion.tipo === 'ok' ? 'admin-msg-ok' : 'modal-error-general')}>
            {accion.tipo === 'ok' ? <CheckCircle2 size={16} /> : <AlertCircle size={16} />}
            <span>{accion.msg}</span>
            {accion.detalle?.posts_sincronizados !== undefined && (
              <span style={{ marginLeft: 8, opacity: 0.7 }}>
                ({accion.detalle.posts_sincronizados} {t('admin.redes.posts')})
              </span>
            )}
          </div>
        )}

        <div className="admin-redes-acciones">
          <button type="button" className="btn-secondary"
                  onClick={() => ejecutarAccion(probarRedesConexion, 'probar')}
                  disabled={enviando || !tieneToken}>
            <Plug size={14} /> {t('admin.redes.btn_probar')}
          </button>
          <button type="button" className="btn-secondary"
                  onClick={() => ejecutarAccion(sincronizarRedes, 'sincronizar')}
                  disabled={enviando || !tieneToken || !activo}>
            <Send size={14} /> {t('admin.redes.btn_sincronizar')}
          </button>
          <button type="button" className="btn-secondary"
                  onClick={() => ejecutarAccion(refrescarRedesToken, 'refrescar')}
                  disabled={enviando || !tieneToken}>
            <RefreshCw size={14} /> {t('admin.redes.btn_refrescar')}
          </button>
        </div>
      </section>

      <section className="admin-card">
        <h2 className="admin-card-titulo">{t('admin.redes.guia_titulo')}</h2>
        <ol className="admin-redes-guia">
          <li>{t('admin.redes.guia.paso1')}</li>
          <li>{t('admin.redes.guia.paso2')}</li>
          <li>{t('admin.redes.guia.paso3')}</li>
          <li>{t('admin.redes.guia.paso4')}</li>
          <li>{t('admin.redes.guia.paso5')}</li>
        </ol>
        <p className="modal-hint">
          <a href="https://developers.facebook.com/docs/instagram-platform/instagram-api-with-instagram-login/business-login"
             target="_blank" rel="noopener noreferrer">
            <ExternalLink size={12} /> {t('admin.redes.guia_doc_link')}
          </a>
        </p>
      </section>
    </div>
  );
}
